'use client'

import { useTranslations } from 'next-intl'
import { useParams } from 'next/navigation'
import { useEffect, useState } from 'react'

import UtmHandler from '@/libs/UtmHandler'

import { BaseColorSetter } from '../../destination/components/BaseColorSetter'
import FeaturedDeals from './components/FeaturedDeals'
import HeroBanner from './components/HeroBanner'
import { IGiftApiResponse, IGiftCategory, IGiftDeal } from './types/schema'
import { createHash } from 'crypto'

interface GiftCertificateClientProps {
  giftData?: IGiftApiResponse
  processedDeals?: IGiftDeal[]
  promotions?: IGiftDeal[]
  locale?: string
}

export default function GiftCertificateClient({
  giftData: serverGiftData,
  processedDeals: serverProcessedDeals,
  promotions: serverPromotions,
  locale: propLocale
}: GiftCertificateClientProps = {}) {
  const t = useTranslations('')
  const params = useParams()
  const locale = propLocale || (params.locale as string)
  const [selectedCategory, setSelectedCategory] = useState<string | null>(
    serverGiftData?.categorys?.[0]?.category || null
  )
  const [giftData, setGiftData] = useState<IGiftApiResponse | null>(serverGiftData || null)
  const [loading, setLoading] = useState(!serverGiftData) // Only show loading if no server data
  const [error, setError] = useState<string | null>(null)

  // Process raw API data into UI-friendly format with global discount support
  const processGiftData = (data: IGiftApiResponse) => {
    const processedDeals: IGiftDeal[] = data.items.map((item) => {
      let salePrice = undefined
      let discountPercent = undefined
      let savings = undefined
      let isPromotion = false

      // Priority 1: Individual item discount (disc/disc_t)
      if (item.disc_t && item.disc !== null && item.disc !== undefined) {
        const discountValue = typeof item.disc === 'string' ? parseFloat(item.disc) : item.disc
        const discountType = typeof item.disc_t === 'string' ? parseInt(item.disc_t) : item.disc_t

        if (discountType === 2) {
          // Percentage
          discountPercent = discountValue
          salePrice = item.price - (item.price * discountValue) / 100
          savings = (item.price * discountValue) / 100
        } else if (discountType === 1) {
          // THB
          salePrice = item.price - discountValue
          savings = discountValue
        }
        isPromotion = true
      }
      // Priority 2: Global discount (if no individual discount)
      else if (data.global_discount && data.global_discount.is_active) {
        const globalDiscount = data.global_discount
        if (globalDiscount.discount_type === 2) {
          // Percentage
          discountPercent = globalDiscount.discount_value
          salePrice = item.price - (item.price * globalDiscount.discount_value) / 100
          savings = (item.price * globalDiscount.discount_value) / 100
        } else if (globalDiscount.discount_type === 1) {
          // THB
          salePrice = item.price - globalDiscount.discount_value
          savings = globalDiscount.discount_value
        }
        isPromotion = salePrice !== undefined && salePrice < item.price
      }

      return {
        id: item.id,
        name: item.name,
        detail: item.detail,
        originalPrice: item.price,
        salePrice,
        discountPercent,
        savings,
        hasImage: item.img === 1,
        duration: item.hour,
        expiry: item.expiry,
        category: item.category,
        isPromotion,
        promoEndDate: item.promo_end,
        imgURI: item.imgURI
      }
    })

    return processedDeals
  }

  // Fetch gift data from API only if not provided by server
  useEffect(() => {
    if (serverGiftData) {
      return
    }
    // Client-side fetch logic would go here if needed
  }, [locale, serverGiftData])

  if (loading) {
    return (
      <div className='ui--gift-certificate'>
        <div className='flex min-h-screen items-center justify-center px-4'>
          <div className='loading-shimmer h-6 w-40 rounded sm:h-8 sm:w-48'></div>
        </div>
      </div>
    )
  }

  if (error || !giftData) {
    return (
      <div className='ui--gift-certificate'>
        <div className='flex min-h-screen items-center justify-center px-4'>
          <div className='text-center'>
            <p className='mb-4 text-sm text-red-600 sm:text-base'>
              Error loading gift certificates
            </p>
            <button
              onClick={() => window.location.reload()}
              className='min-h-[44px] touch-manipulation rounded-lg bg-amber-500 px-6 py-3 text-sm font-medium text-white transition-colors hover:bg-orange-600 sm:min-h-0 sm:px-4 sm:py-2 sm:text-base'>
              Try Again
            </button>
          </div>
        </div>
      </div>
    )
  }

  // Use server-processed data if available, otherwise process client data
  const processedDeals = serverProcessedDeals || processGiftData(giftData)

  // Filter promotions for HeroBanner - Individual item discount only (disc/disc_t)
  const individualPromotions = processedDeals.filter((deal) => {
    const originalItem = giftData?.items.find((item) => item.id === deal.id)
    const hasIndividualDiscount =
      originalItem?.disc_t && originalItem?.disc !== null && originalItem?.disc !== undefined

    return hasIndividualDiscount && deal.salePrice !== undefined
  })

  // All promotions (both individual and global) for FeaturedDeals
  const promotions =
    serverPromotions ||
    processedDeals.filter((deal) => deal.isPromotion && deal.salePrice !== undefined)

  const handleBuyNow = (product: IGiftDeal) => {
    const phpBaseUrl = process.env.NEXT_PUBLIC_API_GATEWAY || 'https://dev.oasisspa.net'
    if(window.dataLayer) {
      const hash = createHash('sha256')
      hash.update(product.id.toString())
      const hashedId = hash.digest('hex')
      window.dataLayer.push({
        event: 'add_to_cart',
        ecommerce: {
          type: 'eGift',
          currency: 'THB',
          items: [{
            item_id:  hashedId,
            item_name: product.name,
            price: product.salePrice || product.originalPrice,
            quantity: 1,
            category: product.category,
          }]
        }
      })
    }
    const cartUrl = `${phpBaseUrl}/${locale}/onlinegift/cart/?gift_id=${product.id}`
    window.location.href = cartUrl
  }

  return (
    <div className='overflow-x-hidden'>
      <BaseColorSetter />
      <UtmHandler />

      {/* Only show HeroBanner when there are individual promotions */}
      {individualPromotions.length > 0 && (
        <HeroBanner
          deals={individualPromotions.slice(0, individualPromotions.length)}
          onBuyNow={handleBuyNow}
        />
      )}

      {individualPromotions.length <= 0 && (
        <div className='mt-20'>
        </div>
      )}

      <section className='ui--gift-certificate-section py-1'>
        <div className='content'>
          {/*Product List*/}
          <FeaturedDeals
            categories={giftData.categorys}
            promotions={promotions}
            selectedCategory={selectedCategory}
            onCategoryChange={setSelectedCategory}
            locale={locale}
            globalDiscount={giftData.global_discount}
          />
        </div>
      </section>
    </div>
  )
}
